import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import StatsCard from '@/components/dashboard/StatsCard';
import { 
  Heart, 
  DollarSign, 
  FileCheck, 
  ArrowRight, 
  Calendar,
  CheckCircle2,
  Clock,
  AlertCircle,
  Sparkles,
  MessageCircle,
  FileText,
  Download,
  Eye
} from 'lucide-react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { calculateEmployeeTaxes } from '@/components/utils/dynamicTaxCalculations';
import EnrollmentAIChat from '@/components/ai/EnrollmentAIChat';
import PayStubPreview from '@/components/enrollment/PayStubPreview';

export default function EmployeeDashboard() {
  const [employee, setEmployee] = useState(null);
  const [employer, setEmployer] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [products, setProducts] = useState([]);
  const [totalTaxSavings, setTotalTaxSavings] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const user = await api.auth.me();
      const employees = await api.entities.Employee.filter({ user_email: user.email });
      
      if (employees.length > 0) {
        const emp = employees[0];
        setEmployee(emp);
        
        const [enrollmentsData, productsData, employerDataArray] = await Promise.all([
          api.entities.Enrollment.filter({ employee_id: emp.id, status: 'active' }),
          api.entities.BenefitProduct.list(),
          api.entities.Employer.filter({ id: emp.employer_id })
        ]);
        
        setEmployer(employerDataArray[0] || null);
        
        console.log('Dashboard loaded enrollments:', enrollmentsData);
        setEnrollments(enrollmentsData);
        setProducts(productsData);
        
        // Calculate total tax savings
        const activePremiums = enrollmentsData
          .filter(e => e.status === 'active')
          .reduce((sum, e) => sum + (e.premium_amount || 0), 0);
        
        if (activePremiums > 0) {
          try {
            const taxAnalysis = await calculateEmployeeTaxes(emp, activePremiums * 12);
            setTotalTaxSavings(taxAnalysis.savings.total);
          } catch {}
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const activeEnrollments = enrollments.filter(e => e.status === 'active');
  const totalPremium = activeEnrollments.reduce((sum, e) => sum + (Number.parseFloat(e.monthly_premium) || Number.parseFloat(e.employee_cost) || 0), 0);
  const flexBudget = Number.parseFloat(employee?.flex_allocation || employee?.optimal_flex_amount) || 0;
  const budgetUsed = flexBudget > 0 ? (totalPremium / flexBudget) * 100 : 0;
  const budgetRemaining = flexBudget - totalPremium;

  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product?.name || 'Unknown Product';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col md:flex-row md:items-center md:justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
            Welcome back, {employee?.first_name || 'there'}!
          </h1>
          <p className="text-slate-500 mt-1">
            Here's an overview of your benefits and enrollment status.
          </p>
        </div>
        
        <Link to={createPageUrl('BenefitsCatalog')}>
          <Button className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600">
            <Sparkles className="w-4 h-4 mr-2" />
            Browse Benefits
          </Button>
        </Link>
      </motion.div>

      {/* Profile Confirmation Banner */}
      {!employee?.profile_confirmed && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
            <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-medium text-amber-900">Complete Your Profile</p>
                  <p className="text-sm text-amber-700">Please verify and confirm your information to proceed with enrollment.</p>
                </div>
              </div>
              <Link to={createPageUrl('EmployeeProfile')} className="self-start sm:self-auto">
                <Button variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-100">
                  Update Profile
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Stats Grid - Simplified to 4 columns */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <StatsCard
            title="Active Benefits"
            value={activeEnrollments.length}
            subtitle="Currently enrolled"
            icon={Heart}
            gradient="from-emerald-500 to-teal-500"
          />
        </motion.div>
        
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <StatsCard
            title="Monthly Premium"
            value={`$${totalPremium.toFixed(2)}`}
            subtitle="Total deductions"
            icon={DollarSign}
            gradient="from-blue-500 to-indigo-500"
          />
        </motion.div>
        
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <div className="group relative">
            <StatsCard
              title="Budget Remaining"
              value={flexBudget > 0 ? `$${budgetRemaining.toFixed(2)}` : 'Not Set'}
              subtitle={flexBudget > 0 ? `${Math.max(0, (100 - budgetUsed)).toFixed(0)}% available` : 'Contact HR to set premium budget'}
              icon={FileCheck}
              gradient="from-purple-500 to-pink-500"
            />
            <div className="absolute top-2 right-2 z-20">
              <div className="relative group/info">
                <div className="w-5 h-5 rounded-full bg-slate-700/80 flex items-center justify-center cursor-help">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="absolute right-0 top-full mt-2 w-72 p-4 bg-slate-900 text-white text-xs rounded-lg shadow-xl opacity-0 invisible group-hover/info:opacity-100 group-hover/info:visible transition-all duration-200 z-50">
                  <p className="font-semibold mb-2">What is Budget Remaining?</p>
                  <p className="leading-relaxed">
                    Your <span className="font-semibold text-emerald-400">Premium Budget</span> (${flexBudget.toFixed(2)}/mo) is the pre-tax amount allocated to you for benefits.
                    <br/><br/>
                    <span className="font-semibold">Budget Remaining</span> shows how much you have left after enrolling in benefits (${totalPremium.toFixed(2)}/mo).
                    <br/><br/>
                    💡 Use the Premium Calculator to optimize your budget!
                  </p>
                  <div className="absolute -top-2 right-4 w-4 h-4 bg-slate-900 transform rotate-45"></div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
        
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <StatsCard
            title="Enrollment Status"
            value={activeEnrollments.length > 0 ? 'Enrolled' : employee?.enrollment_status === 'completed' ? 'Complete' : 'Not Started'}
            subtitle={activeEnrollments.length > 0 ? 'HR will process & submit' : employee?.enrollment_status === 'completed' ? 'Pending activation' : 'Get started today'}
            icon={activeEnrollments.length > 0 ? CheckCircle2 : Clock}
            gradient={activeEnrollments.length > 0 ? "from-green-500 to-emerald-500" : "from-amber-500 to-orange-500"}
          />
        </motion.div>

        {totalTaxSavings > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
            <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-emerald-700">ðŸ’° Annual Tax Savings</p>
                    <p className="text-2xl font-bold text-emerald-900">${totalTaxSavings.toFixed(0)}</p>
                    <p className="text-xs text-emerald-600 mt-1">From Section 125 benefits</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>

      {/* Budget Progress & Paycheck Simulator - Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Budget Progress - Enhanced */}
        {flexBudget > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="h-full"
          >
            <Card className="border-2 border-emerald-100 h-full flex flex-col">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-emerald-500" />
                  Benefits Budget Overview
                </CardTitle>
                <Badge className="bg-emerald-100 text-emerald-700">
                  {Math.max(0, (100 - budgetUsed)).toFixed(0)}% Available
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="flex-grow">
              <div className="space-y-6">
                {/* Visual Progress with gradient */}
                <div className="relative">
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500"></div>
                      <span className="text-sm font-medium text-slate-700">Monthly Premiums</span>
                    </div>
                    <span className="text-sm font-bold text-emerald-700">${totalPremium.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-slate-200"></div>
                      <span className="text-sm font-medium text-slate-700">Remaining Budget</span>
                    </div>
                    <span className="text-sm font-bold text-slate-600">${Math.max(0, budgetRemaining).toFixed(2)}</span>
                  </div>
                  
                  {/* Enhanced Progress Bar */}
                  <div className="relative">
                    <div className="h-6 bg-slate-100 rounded-full overflow-hidden border border-slate-200">
                      <div 
                        className="h-full bg-gradient-to-r from-emerald-500 via-emerald-400 to-teal-500 transition-all duration-500 flex items-center justify-end px-3"
                        style={{ width: `${Math.min(budgetUsed, 100)}%` }}
                      >
                        {budgetUsed > 15 && (
                          <span className="text-xs font-semibold text-white">
                            ${totalPremium.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Flex Budget Label */}
                    <div className="flex justify-between mt-2 text-xs text-slate-500">
                      <span>$0</span>
                      <span className="font-semibold">Premium Budget: ${flexBudget.toFixed(2)}/mo</span>
                    </div>
                  </div>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg border border-emerald-200">
                    <p className="text-xs text-emerald-600 mb-1">Enrolled Benefits</p>
                    <p className="text-lg font-bold text-emerald-700">{activeEnrollments.length}</p>
                  </div>
                  <div className="p-3 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                    <p className="text-xs text-blue-600 mb-1">Budget Efficiency</p>
                    <p className="text-lg font-bold text-blue-700">{budgetUsed.toFixed(0)}%</p>
                  </div>
                </div>

                {/* Info Message */}
                {budgetRemaining > 0 ? (
                  <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm text-blue-800">
                      You have <span className="font-bold">${budgetRemaining.toFixed(2)}/month</span> remaining. 
                      <Link to={createPageUrl('BenefitsCatalog')} className="font-semibold underline ml-1">Browse more benefits</Link>
                    </p>
                  </div>
                ) : budgetRemaining < 0 ? (
                  <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <p className="text-sm text-amber-800">
                      You're over budget by <span className="font-bold">${Math.abs(budgetRemaining).toFixed(2)}/month</span>. Consider adjusting your enrollments.
                    </p>
                  </div>
                ) : (
                  <div className="flex items-start gap-2 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                    <p className="text-sm text-emerald-800">
                      Perfect! You're using your full premium budget allocation.
                    </p>
                  </div>
                )}

                {/* Budget Tips & Actions */}
                {activeEnrollments.length > 0 && (
                  <div className="space-y-3">
                    <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Smart Budget Tips</p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2 py-2 px-3 bg-blue-50 rounded-lg border border-blue-100">
                        <svg className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                          <p className="text-xs font-medium text-blue-900">Pre-Tax Advantage</p>
                          <p className="text-xs text-blue-700 leading-relaxed">You're saving ~${((totalPremium * 12 * 0.3).toFixed(0))}/year in taxes</p>
                        </div>
                      </div>
                      {budgetRemaining > 50 && (
                        <div className="flex items-start gap-2 py-2 px-3 bg-emerald-50 rounded-lg border border-emerald-100">
                          <Sparkles className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-xs font-medium text-emerald-900">Budget Available</p>
                            <p className="text-xs text-emerald-700 leading-relaxed">You could add more coverage</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Helpful Tips when no enrollments */}
                {activeEnrollments.length === 0 && (
                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-4 border border-slate-200">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <Sparkles className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-800 mb-1">Get Started with Benefits</p>
                        <p className="text-xs text-slate-600 leading-relaxed">
                          Maximize your premium budget! Enroll in benefits to save on taxes and protect yourself and your family.
                        </p>
                        <Link to={createPageUrl('BenefitsCatalog')}>
                          <Button size="sm" className="mt-3 bg-emerald-500 hover:bg-emerald-600 text-xs h-8">
                            Explore Benefits
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          </motion.div>
        )}

        {/* Paycheck Simulator */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <PayStubPreview employee={employee} monthlyPremium={totalPremium} />
        </motion.div>
      </div>

      {/* Three Column Layout for Bottom Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-2">
        {/* Active Enrollments */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">My Benefits</CardTitle>
              <Link to={createPageUrl('MyBenefits')}>
                <Button variant="ghost" size="sm">
                  View All <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {activeEnrollments.length === 0 ? (
                <div className="text-center py-12">
                  <Heart className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 mb-4">You haven't enrolled in any benefits yet.</p>
                  <Link to={createPageUrl('BenefitsCatalog')}>
                    <Button className="bg-emerald-500 hover:bg-emerald-600">
                      Browse Benefits
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {activeEnrollments.slice(0, 4).map((enrollment) => (
                    <div 
                      key={enrollment.id} 
                      className="flex items-center justify-between p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                          <Heart className="w-5 h-5 text-white" />
                        </div>
                        <div>
                         <p className="font-medium text-slate-900">{getProductName(enrollment.product_id)}</p>
                         <p className="text-sm text-slate-500">
                           Effective: {enrollment.effective_date ? format(new Date(enrollment.effective_date), 'MMM d, yyyy') : 'Pending'}
                         </p>
                         {enrollment.waiting_period_days > 0 && (
                           <Badge variant="outline" className="mt-1 text-xs">
                             {enrollment.waiting_period_days} day waiting period
                           </Badge>
                         )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-slate-900">${(Number.parseFloat(enrollment.monthly_premium) || Number.parseFloat(enrollment.employee_cost) || 0).toFixed(2)}/mo</p>
                        <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Enrollment Documents */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.7 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-500" />
                Enrollment Documents
              </CardTitle>
            </CardHeader>
            <CardContent>
              {activeEnrollments.some(e => e.enrollment_pdf_url) ? (
                <div className="space-y-3">
                  {activeEnrollments
                    .filter(e => e.enrollment_pdf_url)
                    .reduce((acc, enrollment) => {
                      // Group by PDF URL to avoid duplicates
                      if (!acc.some(item => item.enrollment_pdf_url === enrollment.enrollment_pdf_url)) {
                        acc.push(enrollment);
                      }
                      return acc;
                    }, [])
                    .map((enrollment, idx) => (
                      <div 
                        key={enrollment.id}
                        className="flex items-center justify-between p-4 rounded-xl bg-blue-50 hover:bg-blue-100 transition-colors border border-blue-200"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center">
                            <FileText className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">Benefits Enrollment Form</p>
                            <p className="text-sm text-slate-500">
                              Signed: {enrollment.signed_at ? format(new Date(enrollment.signed_at), 'MMM d, yyyy h:mm a') : 'N/A'}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = enrollment.enrollment_pdf_url;
                              link.download = `enrollment_${employee.last_name}_${format(new Date(enrollment.signed_at || new Date()), 'yyyyMMdd')}.pdf`;
                              link.click();
                            }}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Download
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const win = window.open();
                              win.document.write(`<iframe src="${enrollment.enrollment_pdf_url}" width="100%" height="100%" style="border:none;"></iframe>`);
                            }}
                            className="text-emerald-600 hover:text-emerald-700"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">No enrollment documents yet</p>
                  <p className="text-slate-400 text-xs mt-1">Documents will appear here after enrollment</p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.8 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Link to={createPageUrl('BenefitsCatalog')} className="block">
                <Button variant="outline" className="w-full justify-start h-auto py-4">
                  <Sparkles className="w-5 h-5 mr-3 text-emerald-500" />
                  <div className="text-left">
                    <p className="font-medium">Browse Benefits</p>
                    <p className="text-xs text-slate-500">Explore available options</p>
                  </div>
                </Button>
              </Link>
              
              <Link to={createPageUrl('EmployeeProfile')} className="block">
                <Button variant="outline" className="w-full justify-start h-auto py-4">
                  <FileCheck className="w-5 h-5 mr-3 text-blue-500" />
                  <div className="text-left">
                    <p className="font-medium">Update Profile</p>
                    <p className="text-xs text-slate-500">Verify your information</p>
                  </div>
                </Button>
              </Link>
              
              <Link to={createPageUrl('Help')} className="block">
                <Button variant="outline" className="w-full justify-start h-auto py-4">
                  <MessageCircle className="w-5 h-5 mr-3 text-purple-500" />
                  <div className="text-left">
                    <p className="font-medium">Get Help</p>
                    <p className="text-xs text-slate-500">Check our FAQs</p>
                  </div>
                </Button>
              </Link>
              
              {employer?.schedule_link && (
                <a href={employer.schedule_link} target="_blank" rel="noopener noreferrer" className="block">
                  <Button variant="outline" className="w-full justify-start h-auto py-4">
                    <Calendar className="w-5 h-5 mr-3 text-amber-500" />
                    <div className="text-left">
                      <p className="font-medium">Schedule Call</p>
                      <p className="text-xs text-slate-500">Book a consultation</p>
                    </div>
                  </Button>
                </a>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* AI Chat Widget */}
      <EnrollmentAIChat context={{ currentPage: 'dashboard', employeeData: employee, enrollments }} />
    </div>
  );
}